import React from 'react';
import { ArrowLeft, Ruler } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

export const SizeGuide = () => {
  const navigate = useNavigate();

  const sizeChart = [
    { size: 'L', bust: '38-40', waist: '32-34', hip: '40-42', length: '44-46' },
    { size: 'XL', bust: '40-42', waist: '34-36', hip: '42-44', length: '44-46' },
    { size: '2XL', bust: '42-44', waist: '36-38', hip: '44-46', length: '44-46' },
    { size: '3XL', bust: '44-46', waist: '38-40', hip: '46-48', length: '44-46' },
  ];

  return (
    <div className="min-h-screen bg-[#FAF9F7] pt-32 pb-16">
      <div className="container mx-auto px-4">
        <Button
          variant="ghost"
          className="mb-6 text-[#2C2C2C] hover:text-[#C4B5A0]"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="bg-[#F5F3F0] p-6 rounded-full">
                <Ruler className="h-12 w-12 text-[#C4B5A0]" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-seasons font-light text-[#2C2C2C] mb-4">
              Size Guide
            </h1>
            <p className="text-[#8B7D6B] font-montserrat">
              Find your perfect fit with our comprehensive size chart
            </p>
          </div>

          <Card className="bg-white border-none shadow-lg p-8 md:p-12 mb-8">
            <h2 className="text-2xl font-seasons text-[#2C2C2C] mb-6">Women's Ethnic Wear Size Chart</h2>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[#F5F3F0]">
                    <th className="border border-[#D4C4B0] p-4 text-left font-montserrat text-sm uppercase tracking-wider text-[#6B4E37]">Size</th>
                    <th className="border border-[#D4C4B0] p-4 text-left font-montserrat text-sm uppercase tracking-wider text-[#6B4E37]">Bust (inches)</th>
                    <th className="border border-[#D4C4B0] p-4 text-left font-montserrat text-sm uppercase tracking-wider text-[#6B4E37]">Waist (inches)</th>
                    <th className="border border-[#D4C4B0] p-4 text-left font-montserrat text-sm uppercase tracking-wider text-[#6B4E37]">Hip (inches)</th>
                    <th className="border border-[#D4C4B0] p-4 text-left font-montserrat text-sm uppercase tracking-wider text-[#6B4E37]">Length (inches)</th>
                  </tr>
                </thead>
                <tbody>
                  {sizeChart.map((row, index) => (
                    <tr key={row.size} className={index % 2 === 0 ? 'bg-white' : 'bg-[#FAF9F7]'}>
                      <td className="border border-[#D4C4B0] p-4 font-montserrat font-semibold text-[#2C2C2C]">{row.size}</td>
                      <td className="border border-[#D4C4B0] p-4 font-montserrat text-[#2C2C2C]">{row.bust}</td>
                      <td className="border border-[#D4C4B0] p-4 font-montserrat text-[#2C2C2C]">{row.waist}</td>
                      <td className="border border-[#D4C4B0] p-4 font-montserrat text-[#2C2C2C]">{row.hip}</td>
                      <td className="border border-[#D4C4B0] p-4 font-montserrat text-[#2C2C2C]">{row.length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="bg-white border-none shadow-lg p-8">
            <h3 className="text-xl font-seasons text-[#2C2C2C] mb-4">How to Measure</h3>
            <div className="grid md:grid-cols-2 gap-6 text-[#2C2C2C] font-montserrat text-sm">
              <div>
                <h4 className="font-semibold text-[#6B4E37] mb-2">Bust</h4>
                <p className="text-[#8B7D6B]">Measure around the fullest part of your bust, keeping the tape parallel to the floor.</p>
              </div>
              <div>
                <h4 className="font-semibold text-[#6B4E37] mb-2">Waist</h4>
                <p className="text-[#8B7D6B]">Measure around the narrowest part of your waist, typically just above your belly button.</p>
              </div>
              <div>
                <h4 className="font-semibold text-[#6B4E37] mb-2">Hip</h4>
                <p className="text-[#8B7D6B]">Measure around the fullest part of your hips, approximately 8 inches below your waist.</p>
              </div>
              <div>
                <h4 className="font-semibold text-[#6B4E37] mb-2">Length</h4>
                <p className="text-[#8B7D6B]">Measure from the shoulder down to the desired length of the garment.</p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-[#F5F3F0] rounded-lg">
              <p className="text-sm text-[#2C2C2C] font-montserrat">
                <strong className="text-[#6B4E37]">Note:</strong> All measurements are in inches. For the best fit, we recommend having someone help you take your measurements. If you're between sizes, we suggest ordering the larger size.
              </p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};